// The list of orderable items that are not covered by Medicare but are covered by Tricare
const tricareOnlyTests = (process.env.REACT_APP_STATUTORILY_NON_COVERED_MEDICARE_ITEMS || '').split(',').map(Number);

/**
 * Returns whether the order's practitioner and requisitions support the Medicare billing method
 */
const isMedicareSupported = (gearOrder) => {
	// Check if the practitioner supports Medicare
	let medicareSupported = isPractitionerSupportsMedicare(gearOrder?.practitioner);

	// Check if all requistiions on the order support Medicare (The check is skipped if the order does not contain any requistions)
	return medicareSupported && isOrderRequisitionsMedicareEligible(gearOrder);
}

/**
 * Return true if all requistiions on the order support Medicare (true is returned if the order does not contain any requistions)
 * 
 * @param gearOrder
 */
const isOrderRequisitionsMedicareEligible = (gearOrder) => {
	return (
		!gearOrder?.requisitions?.length ||
		gearOrder.requisitions.every((req) => {
			return isMedicareOrTricareOrderableItem({
				mcrEligible: req.medicareEligible,
				id: req.omsOrderableItemId
			});
		})
	);
}

/**
 * Returns whether the Orderable Item is covered by Medicare or Tricare.
 * An item is considered covered if it has mcrEligible=true or the item's id is in the tricareOnlyTests list.
 */
const isMedicareOrTricareOrderableItem = (item) => {
	return item && (item.mcrEligible || (item.id && tricareOnlyTests.indexOf(item.id) > -1));
}

/**
 * Returns whether the practitioner supports the Medicare billing option.
 */
const isPractitionerSupportsMedicare = (practitioner) => {
	return practitioner?.billingMethods?.includes('Medicare');
}

function isMedicareOrTricareSubscriberId(subscriberId) {
	return isMedicareBeneficiaryIdentifier(subscriberId) || isTricareSubscriberIdFormat(subscriberId);
}

/**
 * Tests if a String is a valid Medicare Beneficiary Identifier.
 *
 * Strip all non-alphanumeric characters and apply the following rules (case insensitive):
 * ```
 * Position 1 - numeric values 1 thru 9
 * Position 2 - alphabetic values A thru Z (minus S, L, O, I, B, Z)
 * Position 3 - alpha-numeric values 0 thru 9 and A thru Z (minus S, L, O, I, B, Z)
 * Position 4 - numeric values 0 thru 9
 * Position 5 - alphabetic values A thru Z (minus S, L, O, I, B, Z)
 * Position 6 - alpha-numeric values 0 thru 9 and A thru Z (minus S, L, O, I, B, Z)
 * Position 7 - numeric values 0 thru 9
 * Position 8 - alphabetic values A thru Z (minus S, L, O, I, B, Z)
 * Position 9 - alphabetic values A thru Z (minus S, L, O, I, B, Z)
 * Position 10 - numeric values 0 thru 9
 * Position 11 - numeric values 0 thru 9
 * ```
 *
 * @param subscriberId The Subscriber ID string to test
 * @return true if the string is a valid MBI, false otherwise
 * @link https://www.cms.gov/medicare/new-medicare-card/understanding-the-mbi.pdf
 */
 function isMedicareBeneficiaryIdentifier(subscriberId) {
	// Strip non-alphanumeric characters
	const mbi = subscriberId?.replace(/[^0-9a-z]/gi, '');

	if (mbi?.length !== 11) {
		return false;
	}

	// Use 1-based index to keep the switch positions the same as the documentation for easy reading
	for (let i = 1; i <= mbi.length; i++) {
		const c = mbi.charAt(i - 1);

		// Apply rules based on character position
		switch (i) {
			// numeric values 1 thru 9
			case 1:
				if (!isNumeric_oneThroughNine(c)) {
					return false;
				}
				break;

			// alphabetic values A thru Z (minus S, L, O, I, B, Z)
			case 2:
			case 5:
			case 8:
			case 9:
				if (!isValidAlphabetic(c)) {
					return false;
				}
				break;

			// alpha-numeric values 0 thru 9 and A thru Z (minus S, L, O, I, B, Z)
			case 3:
			case 6:
				if (!isValidAlphabetic(c) && !isNumeric_zeroThroughNine(c)) {
					return false;
				}
				break;

			// numeric values 0 thru 9
			case 4:
			case 7:
			case 10:
			case 11:
				if (!isNumeric_zeroThroughNine(c)) {
					return false;
				}
				break;
			default:
				return false;
		}
	}

	return true;
}

/**
 * SCR-6871: Tests if a String is a valid Tricare subscriber ID.
 *
 * An ID matches the Tricare format if it is 9 or 11 characters long and is all numeric (ignoring dashes).
 *
 * @param subscriberId The Subscriber ID string to test
 * @return true if the string is a valid Tricare ID, false otherwise
 */
function isTricareSubscriberIdFormat(subscriberId) {
	const id = subscriberId?.replace(/-/g, '') ?? '';

	return (id.length === 9 || id.length === 11) && /^[0-9]*$/.test(id);
}

/**
 * Tests if the character is an Alphabetic Character (A...Z), excluding (S, L, O, I, B, Z)
 *
 * @param val The character to test
 * @return true if the character is a valid Alphabetic Character, false otherwise
 */
function isValidAlphabetic(val) {
	const regex = /^[ac-hjkmnpqrt-y]$/i;
	return regex.test(val);
}

/**
 * Tests if the character is Numeric (0-9).
 *
 * @param val The character to test
 * @return true if the character is Numeric, false otherwise
 */
function isNumeric_zeroThroughNine(val) {
	return /^[0-9]$/.test(val);
}

/**
 * Tests if the character is Numeric 1 through 9.
 *
 * @param val The character to test
 * @return true if the character is 1-9, false otherwise
 */
function isNumeric_oneThroughNine(val) {
	return /^[1-9]$/.test(val);
}

export { tricareOnlyTests, isMedicareSupported, isMedicareOrTricareOrderableItem, isPractitionerSupportsMedicare, isMedicareOrTricareSubscriberId, isOrderRequisitionsMedicareEligible };